import { PanellistRecommendation } from '@prisma/client/browser'
import { SortOrder } from '../types/types'

export const CandidateDBQuery = {
    parsedResume: false,
    candidateDetails: {
        include: {
            candidateExperiences: {
                include: {
                    roles: {
                        include: {
                            projects: true,
                        },
                    },
                },
            },
            candidateQualifications: true,
            candidateSkills: {
                orderBy: {
                    proficiency: 'desc' as SortOrder,
                },
            },
        },
    },
    candidateNotes: true,
    Source: true,
}

export const JobApplicationDBQuery = {
    applicationDocuments: true,
    parsedResume: false,
    JobPosting: {
        include: {
            parsedJobDescription: false,
            AssessmentTemplate: {
                include: {
                    AssessmentTemplateParameters: {
                        include: {
                            AssessmentParameter: {
                                include: {
                                    ratings: true,
                                },
                            },
                        },
                    },
                },
            },
            Company: {
                include: {
                    CompanyConfig: true,
                },
            },
        },
    },
    candidateDetails: {
        include: {
            candidate: {
                select: { id: true },
            },
            candidateExperiences: {
                include: {
                    roles: {
                        include: {
                            projects: true,
                        },
                    },
                },
            },
            candidateQualifications: true,
            candidateSkills: {
                orderBy: {
                    proficiency: 'desc' as SortOrder,
                },
            },
        },
    },
    source: true,
    Interview: {
        include: {
            InterviewRounds: {
                include: {
                    PanellistFeedback: true,
                    InterviewResult: true,
                },
                orderBy: {
                    roundNum: 'asc' as SortOrder,
                },
            },
            ScreeningInterview: {
                include: {
                    ScreeningResult: {
                        include: {
                            ScreeningResultCategories: { orderBy: { id: 'asc' as SortOrder } },
                        },
                    },
                },
            },
            ScreeningCall: {
                include: {
                    ScreeningResult: {
                        include: {
                            ScreeningResultCategories: { orderBy: { id: 'asc' as SortOrder } },
                        },
                    },
                },
            },
            AssessmentParameter: {
                include: {
                    ratings: {
                        include: {
                            PanellistFeedback: true,
                        },
                    },
                },
            },
        },
    },
    JobOffer: {
        include: {
            JobOfferDocuments: true,
        },
    },
}

export const InterviewDBQuery = {
    InterviewRounds: {
        include: {
            PanellistFeedback: true,
            InterviewResult: true,
            InterviewPanelistParameterAssignment: true,
        },
        orderBy: {
            roundNum: 'asc' as SortOrder,
        },
    },
    ScreeningInterview: {
        select: {
            id: true,
            createdAt: true,
            updatedAt: true,
            roomToken: true,
            roomTokenExpiry: true,
            status: true,
            questions: true,
            screeningConfig: true,
            livenessVerificationStatus: true,
            interviewEvents: true,
            identityVerified: true,
            ScreeningResult: {
                select: {
                    createdAt: true,
                    updatedAt: true,
                    overallScore: true,
                    overallSummary: true,
                    interviewTranscription: true,
                    psychometricAnalysis: true,
                    completionState: true,
                    cefrScoring: true,
                    cheatDetection: true,
                    ScreeningResultCategories: { orderBy: { id: 'asc' as SortOrder } },
                    originalTranscript: true,
                },
            },
            faceLivenessVerification: {
                select: {
                    id: true,
                    status: true,
                    confidence: true,
                    auditImages: true,
                    createdAt: true,
                    updatedAt: true,
                    awsSessionId: true,
                },
            },
            proctoringEvents: true,
            interviewTransport: true,
        },
    },
    ScreeningCall: {
        include: {
            ScreeningResult: {
                include: {
                    ScreeningResultCategories: { orderBy: { id: 'asc' as SortOrder } },
                },
            },
            // Newest call events for the phone card's voicemail-callback / manual-call indicator.
            lifecycleEvents: {
                select: { outcome: true, createdAt: true, scheduledAt: true },
                orderBy: { createdAt: 'desc' as SortOrder },
                take: 10,
            },
        },
    },
}

export const panellistRecommendation: Record<string, PanellistRecommendation> = {
    Yes: PanellistRecommendation.SELECTED,
    No: PanellistRecommendation.REJECTED,
    'Select Recommendation': PanellistRecommendation.PENDING,
    Hold: PanellistRecommendation.ON_HOLD,
}

export const bookingLoaderTexts = ['Creating event', 'Setting up details', 'Generating meeting link', 'Sending slot details to candidate']
export const screeningLoaderTexts = [
    'Generating interview token',
    'Finalizing interview link',
    'Generating interview questions',
    'Finalizing interview questions',
]

export const transcriptionLoaderTexts = ['Evaluating transcription', 'Generating summary', 'Generating summary', 'Saving summary']
export const generatingFeedbackLoaderTexts = ['Evaluating Notes', 'Generating feedback', 'Generating feedback', 'Saving feedback']
export const intervalTime = {
    bookingLoaderTime: 2000,
    transcriptionLoaderTime: 1000,
}
export const parameterRatings = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]
export const reversePanellistRecommendation = Object.fromEntries(Object.entries(panellistRecommendation).map(([key, value]) => [value, key]))

export const parameterOptions = [
    {
        id: 1,
        parameter: 'Technical Skills',
        definition: 'The ability to effectively apply specific technical knowledge and tools relevant to the job.',
        questions: [
            "Can you describe a challenging technical problem you've solved and the tools or technologies you used?",
            'What programming languages or tools are you most comfortable with, and how have you used them recently?',
            'Tell me about a project where you implemented a new technology or optimized an existing system.',
        ],
    },
    {
        id: 2,
        parameter: 'Problem Solving',
        definition: 'The capacity to identify problems, analyze situations, and develop effective solutions.',
        questions: [
            'Tell me about a time when you encountered an unexpected obstacle in a project. How did you resolve it?',
            "How do you approach a problem when you don't initially know the solution?",
            'Can you share an example of how you identified and resolved the root cause of a persistent issue?',
        ],
    },
    {
        id: 3,
        parameter: 'Communication',
        definition: 'The ability to clearly convey ideas, information, and instructions to others effectively.',
        questions: [
            'Can you provide an example of how you explained a complex concept to a non-technical audience?',
            'How do you ensure clear and effective communication within a team setting?',
            'Tell me about a time when you had to manage a misunderstanding or miscommunication in the workplace.',
        ],
    },
    {
        id: 4,
        parameter: 'Leadership',
        definition: 'The ability to guide, inspire, and influence others towards achieving shared goals.',
        questions: [
            'Share an experience where you had to lead a team through a challenging situation. How did you ensure success?',
            'How do you inspire and motivate team members to perform at their best?',
            'Describe a time when you had to make a difficult decision as a leader. How did you approach it?',
        ],
    },
    {
        id: 5,
        parameter: 'Team Collaboration',
        definition: 'The ability to work effectively with others to achieve shared objectives.',
        questions: [
            'Can you describe a situation where you worked closely with a team to achieve a shared goal? What was your role?',
            'How do you handle conflicts or disagreements within a team?',
            'Share an example of a time when your collaboration with others led to a better outcome than working individually.',
        ],
    },
    {
        id: 6,
        parameter: 'Cultural Fit',
        definition: "The alignment of an individual's values, work style, and behaviors with the organization's culture.",
        questions: [
            'What kind of work environment do you thrive in, and how do you contribute to maintaining a positive workplace culture?',
            'How do you adapt to the culture of a new organization or team?',
            'Tell me about a time when you faced a conflict with company values or culture. How did you handle it?',
        ],
    },
    {
        id: 7,
        parameter: 'Learning Ability',
        definition: 'The willingness and capacity to acquire new knowledge and adapt to evolving situations.',
        questions: [
            'Tell me about a time you had to quickly learn a new skill or technology to complete a task. How did you approach it?',
            'How do you stay updated with new developments and trends in your field?',
            'Share an example of how you applied knowledge gained through self-learning to solve a problem.',
        ],
    },
    {
        id: 8,
        parameter: 'Project Management',
        definition: 'The ability to plan, execute, and oversee projects to ensure successful completion.',
        questions: [
            'Can you walk me through a project you managed from start to finish? How did you ensure it stayed on track?',
            'What tools or methods do you use to manage and prioritize tasks in a project?',
            'Tell me about a time when you had to handle competing priorities in a project. How did you manage them?',
        ],
    },
    {
        id: 9,
        parameter: 'Innovation',
        definition: 'The ability to think creatively and introduce new ideas or methods to improve processes or outcomes.',
        questions: [
            'Share an example of a time when you introduced a new idea or process that improved efficiency or productivity.',
            'How do you encourage innovative thinking in yourself and others?',
            'Describe a situation where you challenged the status quo to implement a creative solution.',
        ],
    },
    {
        id: 10,
        parameter: 'Domain Knowledge',
        definition: 'The depth of expertise and understanding in a specific field or industry.',
        questions: [
            'Can you explain a recent development in your field of expertise and how it impacts your work?',
            'What trends or challenges do you see emerging in your industry, and how are you preparing for them?',
            'Tell me about a project where your domain knowledge was critical to its success.',
        ],
    },
    {
        id: 11,
        parameter: 'Customer Greeting & Communication',
        definition: 'The ability to welcome customers professionally and communicate effectively to establish rapport and trust.',
        questions: [
            'Describe your approach to greeting customers when they enter the store. How do you adjust your greeting based on the situation?',
            'Tell me about a time when you had to handle a frustrated customer. How did your communication approach help resolve the situation?',
            'How do you ensure consistent and professional communication with every customer, even during busy periods?',
        ],
    },
    {
        id: 12,
        parameter: 'Customer Engagement',
        definition: 'The ability to connect with customers, understand their needs, and create meaningful interactions that drive satisfaction and loyalty.',
        questions: [
            'Share a specific example of how you engaged with a customer to understand their needs and provide a personalized shopping experience.',
            'How do you maintain engagement with customers who are browsing but not actively seeking assistance?',
            'Describe a situation where your customer engagement skills turned a casual browser into a loyal customer. What specific techniques did you use?',
        ],
    },
    {
        id: 13,
        parameter: 'Upselling',
        definition: 'The skill of suggesting additional or premium products to customers in a way that adds value to their purchase without being pushy.',
        questions: [
            'Tell me about your most successful upselling experience. What approach did you take, and why do you think it worked?',
            "How do you identify upselling opportunities while ensuring you're meeting the customer's actual needs?",
            'Describe how you balance meeting sales targets through upselling while maintaining a positive customer experience.',
        ],
    },
    {
        id: 14,
        parameter: 'Visual Merchandising',
        definition: 'The ability to create visually appealing product displays and store layouts that enhance the customer experience and drive sales.',
        questions: [
            'Can you walk me through a display or visual merchandising project you implemented? What was your thought process and what results did you see?',
            'How do you stay current with visual merchandising trends, and how have you applied these trends in your work?',
            'Describe how you would adjust visual merchandising elements based on seasonal changes or promotional campaigns.',
        ],
    },
    {
        id: 15,
        parameter: 'Store Sales Numbers',
        definition: 'The understanding of sales metrics and the ability to analyze and respond to sales data to improve performance.',
        questions: [
            'How do you track and analyze sales performance in your current or previous role?',
            'Tell me about a time when you identified a sales trend and implemented a strategy that positively impacted store performance.',
            'Describe how you would respond if your department or store was consistently missing sales targets. What steps would you take to address the issue?',
        ],
    },
]

export const paramOptionBasedOnDepartment: Record<string, typeof parameterOptions> = {
    SOFTWARE: parameterOptions.slice(0, 6),
    FINANCE: parameterOptions.slice(3, 6),
    SALES: parameterOptions.slice(6, 9),
    MARKETING: parameterOptions.slice(1, 9),
    SUPPLY_CHAIN: parameterOptions.slice(6, 9),
    MANUFACTURING: parameterOptions.slice(3, 9),
    CUSTOMER_SUPPORT: parameterOptions.slice(4, 9),
    MANAGEMENT: parameterOptions.slice(0, 9),
    HUMAN_RESOURCES: parameterOptions.slice(2, 9),
    ADMINISTRATION: parameterOptions.slice(1, 9),
}

export enum EventType {
    UPDATE = 'update',
    DELETE = 'delete',
}

export const InterviewRoundStatus = {
    PENDING: {
        label: 'Pending',
        bgColor: 'bg-yellow-100 text-yellow-800',
        textColor: 'text-yellow-800',
    },
    INIT: {
        label: 'Initiated',
        bgColor: 'bg-yellow-100 text-yellow-800',
        textColor: 'text-yellow-800',
    },
    NOT_SCHEDULED: {
        label: 'Awaiting Slot Confirmation',
        bgColor: 'bg-yellow-100 text-yellow-800',
        textColor: 'text-yellow-800',
    },
    SCHEDULED: {
        label: 'Scheduled',
        bgColor: 'bg-orange-100 text-orange-800',
        textColor: 'text-orange-800',
    },
    RESULT_COMPLETED: {
        label: 'Result Generated',
        bgColor: 'bg-green-100 text-green-800',
        textColor: 'text-green-800',
    },
    INTERVIEW_STARTED: {
        label: 'Interview Started',
        bgColor: 'bg-yellow-100 text-yellow-800',
        textColor: 'text-yellow-800',
    },
    INTERVIEW_DONE: {
        label: 'Interview Done',
        bgColor: 'bg-blue-100 text-blue-800',
        textColor: 'text-blue-800',
    },
    QUESTIONS_GENERATED: {
        label: 'Scheduled',
        bgColor: 'bg-orange-100 text-orange-800',
        textColor: 'text-orange-800',
    },
}

export const pathToDisableTopNav = ['/career']

export const pathToDisableSideNav = ['/career']
